import React, { useState, useEffect, useRef } from "react";
import {useDrag} from "react-dnd";
import {ItemTypes} from "../team-page/itemTypes.js";
import {findObjectByKeyValue} from "../utils.js";
import {Link} from "react-router-dom";
import { useCurrentTeamId } from "../../hooks/useCurrentTeamId";
import { buildTeamLink, buildPublicTeamLink, useLeaguePrefix } from "../../utils/leagueLinks";
import { supabase } from '../../lib/supabaseClient'; // Import Supabase client
import ballIcon from '../../assets/messages/ball.png'; // Import ball icon

const FRONTEND_FALLBACK_LOGO_ASSET = ballIcon;
const BACKEND_DEFAULT_TEAM_LOGO_PATH = '/uploads/teams/7b9a405a6a953b17e04a8f0107c3e5fd.jpg';

// Helper function to clean up and validate logo URLs
const getCleanLogoUrl = (logoUrl) => {
    if (!logoUrl || logoUrl.trim() === "" || logoUrl === "/uploads/teams/default-team-logo.png") {
        return BACKEND_DEFAULT_TEAM_LOGO_PATH;
    }
    if (logoUrl === FRONTEND_FALLBACK_LOGO_ASSET) {
        return logoUrl;
    }
    if (logoUrl.startsWith('/https://') || logoUrl.startsWith('/http://')) {
        return logoUrl.substring(1);
    }
    if (logoUrl.startsWith('https://') || logoUrl.startsWith('http://')) {
        return logoUrl;
    }
    if (logoUrl.startsWith('/uploads/')) {
        return logoUrl;
    }
    console.warn(`[getCleanLogoUrl] Unrecognized logo URL format: "${logoUrl}". Falling back to backend default.`);
    return BACKEND_DEFAULT_TEAM_LOGO_PATH;
};

// Helper function to extract file path from Supabase URL
const extractSupabaseFilePath = (url) => {
    if (!url || !url.includes('supabase.co')) return null;
    const match = url.match(/\/storage\/v1\/object\/public\/(.*)/);
    return match ? match[1] : null;
};

// Component for handling individual team logo with error tracking and authenticated loading
const TeamLogo = ({ logoUrl, teamName, className = "" }) => {
    const [currentSrc, setCurrentSrc] = useState(FRONTEND_FALLBACK_LOGO_ASSET);
    const [hasErrored, setHasErrored] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const errorCountRef = useRef(0);
    const blobUrlRef = useRef(null);

    useEffect(() => {
        return () => {
            if (blobUrlRef.current && blobUrlRef.current !== FRONTEND_FALLBACK_LOGO_ASSET && blobUrlRef.current.startsWith('blob:')) {
                URL.revokeObjectURL(blobUrlRef.current);
            }
        };
    }, []);

    useEffect(() => {
        setHasErrored(false);
        errorCountRef.current = 0;
        setIsLoading(false);

        const loadImage = async () => {
            if (!logoUrl) {
                setCurrentSrc(FRONTEND_FALLBACK_LOGO_ASSET);
                return;
            }

            const cleanUrl = getCleanLogoUrl(logoUrl);
            if (!cleanUrl.includes('supabase.co')) {
                setCurrentSrc(cleanUrl);
                return;
            }

            const filePath = extractSupabaseFilePath(cleanUrl);
            if (!filePath) {
                console.warn(`Could not extract file path from Supabase URL: ${cleanUrl}`);
                setCurrentSrc(FRONTEND_FALLBACK_LOGO_ASSET);
                return;
            }

            setIsLoading(true);
            
            try {
                const [bucketName, ...pathParts] = filePath.split('/');
                const fileName = pathParts.join('/');
                const { data, error } = await supabase.storage.from(bucketName).download(fileName);
                
                if (error) throw error;
                
                if (blobUrlRef.current && blobUrlRef.current !== FRONTEND_FALLBACK_LOGO_ASSET && blobUrlRef.current.startsWith('blob:')) {
                    URL.revokeObjectURL(blobUrlRef.current);
                }
                
                const blobUrl = URL.createObjectURL(data);
                blobUrlRef.current = blobUrl;
                setCurrentSrc(blobUrl);
                setIsLoading(false);
                
            } catch (error) {
                console.log(`Error loading ${teamName} logo from Supabase:`, error.message);
                setCurrentSrc(FRONTEND_FALLBACK_LOGO_ASSET);
                setHasErrored(true);
                setIsLoading(false);
            }
        };

        loadImage();
    }, [logoUrl, teamName]);

    const handleError = (e) => {
        errorCountRef.current += 1;
        if (errorCountRef.current > 1 || hasErrored) return;
        setHasErrored(true);
        setCurrentSrc(FRONTEND_FALLBACK_LOGO_ASSET);
    };

    if (isLoading) {
        return <img src={FRONTEND_FALLBACK_LOGO_ASSET} alt={`Loading ${teamName || 'team'} logo...`} className={`${className} loading-opacity`} style={{ opacity: 0.5 }} />;
    }

    return <img src={currentSrc} alt={teamName || 'Team logo'} className={className} onError={handleError} />;
};


function TeamsBlock({teams}) {
    const currentTeamId = useCurrentTeamId();
    const prefix = useLeaguePrefix();
    const totalTeams = teams?.length || 0;

    // Determine position zone class based on league standings
    const getPositionClass = (index) => {
        const position = index + 1;
        if (position === 1) return 'position-champion';
        if (position <= 4) return 'position-cl';
        if (position <= 6) return 'position-europa';
        if (totalTeams > 3 && position > totalTeams - 3) return 'relegation-zone';
        return '';
    };

    const offerItems = teams?.map(function (item, index) {
        // Normalise property names and compute derived values
        const losses = item.losses ?? item.loses ?? 0;
        const goalsFor = item.for ?? item.goalsFor ?? 0;
        const goalsAgainst = item.against ?? item.goalsAgainst ?? 0;
        const gd = goalsFor - goalsAgainst;
        const isCurrentTeam = item._id === currentTeamId;
        const positionClass = getPositionClass(index);

        return (
            <div key={index} className={`league-list-row ${positionClass} ${isCurrentTeam ? 'current-team' : ''}`}>
                <div className="col">
                    {index+1}.
                </div>
                <div className="col title">
                    <div className="image">
                        <TeamLogo logoUrl={item.logo} teamName={item.name} />
                    </div>

                    <Link to={item._id === currentTeamId ? buildTeamLink(item._id, prefix) : buildPublicTeamLink(item._id, prefix)}>{item.name}</Link>


                </div>
                <div className="col change">
                    <span className={`ico ${item.pointsChange ? item.pointsChange : 'semi'}`} />
                </div>
                <div className="col">{item.played}</div>
                <div className="col">{item.wins}</div>
                <div className="col">{item.draws}</div>
                <div className="col">{losses}</div>
                <div className="col">{item.for}</div>
                <div className="col">{item.against}</div>
                <div className="col">{gd}</div>
                <div className="col">{item.points}</div>
                
            </div>
        )
    });


    return (

        <div className="leagues-container">

            <div className="header-row league-list-row">
                <div className="info-col">Pos</div>
                <div className="info-col"><span>Team name</span><span className="mobile-hide">Team</span></div>
                <div className="info-col"><span>Played</span><span className="mobile-hide">P</span></div>
                <div className="info-col"><span>Wins</span><span className="mobile-hide">W</span></div>
                <div className="info-col"><span>Draws</span><span className="mobile-hide">D</span></div>
                <div className="info-col"><span>Losses</span><span className="mobile-hide">L</span></div>
                <div className="info-col"><span>For</span><span className="mobile-hide">F</span></div>
                <div className="info-col"><span>Against</span><span className="mobile-hide">A</span></div>
                <div className="info-col"><span>GD</span><span className="mobile-hide">GD</span></div>
                <div className="info-col"><span>Points</span><span className="mobile-hide">PTS</span></div>
            </div>

            {offerItems}

        </div>
    )
}

export {TeamsBlock}
